import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { LuLock, LuLogOut, LuPhone } from "react-icons/lu";

import AuthLayout from "./AuthLayout";
import PinInput from "../pinInput";
import { useAppSelector } from "../../app/hooks";
import { useAuth } from "../../context/AuthContext";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { POST } from "../../services/axios.service";
import { getGujaratiRoleName } from "../../static/role";
import {
  PIN_UNLOCKED_STORAGE_KEY,
  holdUnlockPresence,
  isAnyTabUnlocked,
  releaseUnlockPresence,
} from "../../static/pinPresence";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";

const PinLockScreen = () => {
  const navigate = useNavigate();
  const { setIsAuthenticated } = useAuth();
  const loggingUser = useAppSelector(getLoggingUser);

  const [unlocked, setUnlocked] = useState<boolean>(
    () => sessionStorage.getItem(PIN_UNLOCKED_STORAGE_KEY) === "1"
  );
  const [pin, setPin] = useState<string>("");
  const [presenceChecked, setPresenceChecked] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isProfileReady = !!loggingUser?.id;

  useEffect(() => {
    if (unlocked || !isProfileReady) return;

    if (!loggingUser?.isLoginPinSet) {
      sessionStorage.setItem(PIN_UNLOCKED_STORAGE_KEY, "1");
      setUnlocked(true);
    }
  }, [unlocked, isProfileReady, loggingUser?.isLoginPinSet]);

  useEffect(() => {
    if (unlocked) return;

    let cancelled = false;

    isAnyTabUnlocked().then((anyUnlocked) => {
      if (cancelled) return;

      if (anyUnlocked) {
        sessionStorage.setItem(PIN_UNLOCKED_STORAGE_KEY, "1");
        setUnlocked(true);
      }

      setPresenceChecked(true);
    });

    return () => {
      cancelled = true;
    };
  }, [unlocked]);

  useEffect(() => {
    if (unlocked) holdUnlockPresence();
  }, [unlocked]);

  useEffect(() => {
    if (unlocked) return;

    document.body.classList.add("overflow-hidden");

    return () => document.body.classList.remove("overflow-hidden");
  }, [unlocked]);

  const handleVerifyPin = async (pinValue: string) => {
    if (pinValue.length !== 6 || isVerifying) return;

    setIsVerifying(true);

    try {
      await POST("/v1/profile/verify-pin", { loginPin: pinValue })
        .then((response: any) => {
          if (response.data.status === "success") {
            sessionStorage.setItem(PIN_UNLOCKED_STORAGE_KEY, "1");
            setUnlocked(true);
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
            setPin("");
          }
        })
        .catch((error: any) => {
          toast.error(
            error?.data?.message ||
              "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
            { ...(toastStyles as any) }
          );
          setPin("");
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
      setPin("");
    }

    setIsVerifying(false);
  };

  const handleLogout = async () => {
    setIsLoading(true);

    try {
      await POST("/v1/logout", {})
        .then(async (response: any) => {
          if (response.data.status === "success") {
            await localStorage.removeItem("authToken");
            sessionStorage.removeItem(PIN_UNLOCKED_STORAGE_KEY);
            releaseUnlockPresence();
            await setIsAuthenticated(false);

            navigate(route.login);
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          toast.error(
            error?.data?.message ||
              "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
            { ...(toastStyles as any) }
          );
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    setIsLoading(false);
  };

  if (unlocked) return null;

  const roleLabel = loggingUser?.roles?.[0] || "";
  const showPinInput =
    presenceChecked && isProfileReady && !!loggingUser?.isLoginPinSet;

  return (
    <div className="fixed inset-0 z-[100]">
      <AuthLayout>
        <div className="rounded-lg text-[hsl(222_84%_5%)] shadow-2xl border-0 bg-white/80 backdrop-blur-sm w-full max-w-md mx-auto">
          {showPinInput ? (
            <>
              <div className="flex flex-col p-6 space-y-2 pb-6 pt-8 max-sm:px-5 max-[360px]:px-4 max-sm:pt-6">
                <div className="flex flex-col items-center gap-2 pb-2">
                  <span className="relative flex h-20 w-20 shrink-0 overflow-hidden rounded-full border-2 border-[#113F67]/20">
                    {loggingUser?.image ? (
                      <img
                        src={loggingUser.image}
                        alt={loggingUser?.name || ""}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center rounded-full bg-[#34699A] text-white text-2xl font-bold">
                        {loggingUser?.name?.substring(0, 2)?.toUpperCase() || ""}
                      </span>
                    )}
                  </span>

                  <div className="mt-1 text-center">
                    <p className="text-lg font-bold text-[#113F67]">
                      {loggingUser?.name || ""}
                    </p>
                    <p className="text-sm text-gray-600">
                      ({getGujaratiRoleName(roleLabel) || roleLabel})
                    </p>
                    <p className="text-sm text-gray-600 flex items-center justify-center gap-1 mt-0.5">
                      <LuPhone className="h-3 w-3 text-[#34699A]" />
                      {loggingUser?.phone || ""}
                    </p>
                  </div>
                </div>

                <div className="tracking-tight text-3xl font-bold text-center text-[#113F67] flex items-center justify-center gap-2 max-sm:text-2xl">
                  <LuLock className="h-6 w-6 text-[#34699A]" />
                  એપ્લિકેશન લૉક છે
                </div>
                <div className="text-center text-gray-600 text-base max-sm:text-sm">
                  તમારો 6 અંકનો પિન દાખલ કરો
                </div>
              </div>

              <div className="p-6 pt-0 space-y-6 px-8 pb-2 max-sm:px-5 max-[360px]:px-4">
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleVerifyPin(pin);
                  }}
                  className="space-y-6"
                >
                  <div className="flex justify-center">
                    <PinInput
                      value={pin}
                      onChange={setPin}
                      onComplete={handleVerifyPin}
                      disabled={isVerifying}
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={pin.length !== 6 || isVerifying}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none px-4 py-2 w-full h-14 bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b] text-white font-semibold text-lg rounded-xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isVerifying ? (
                      <div className="flex items-center">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                        ચકાસણી થઈ રહી છે...
                      </div>
                    ) : (
                      "અનલૉક કરો"
                    )}
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="px-8 pt-8 pb-2 max-sm:px-5">
              <div className="flex flex-col items-center gap-4 py-6">
                <div className="w-10 h-10 border-4 border-[#113F67]/30 border-t-[#113F67] rounded-full animate-spin"></div>
                <p className="text-sm text-gray-600">માહિતી લોડ થઈ રહી છે...</p>
              </div>
            </div>
          )}

          <div className="px-8 pb-8 text-center max-sm:px-5">
            <button
              type="button"
              onClick={handleLogout}
              disabled={isVerifying || isLoading}
              className="text-sm text-[#34699A] hover:text-[#113F67] transition-colors hover:underline font-medium inline-flex items-center gap-1 disabled:opacity-50"
            >
              <LuLogOut className="h-4 w-4" />
              {isLoading
                ? "રાહ જુઓ..."
                : "બીજા યુઝર તરીકે લોગિન કરવા લોગઆઉટ કરો"}
            </button>
          </div>
        </div>
      </AuthLayout>
    </div>
  );
};
export default PinLockScreen;
